import React, { useEffect, useMemo, useState } from "react";
import Input from "../../extra/Input";
import {
  getSettingApi,
  editSetting,
  switchApi,
  getAdsApi,
  isAdsChange,
  adsApiData,
  getWithdrawalApi,
} from "../../store/setting/setting.action";
import { FormControlLabel, Switch } from "@mui/material";
import styled from "@emotion/styled";
import { connect, useDispatch, useSelector } from "react-redux";
import Button from "../../extra/Button";
import { Skeleton } from "@mui/material";
import { permissionError } from "../../../util/Alert";
import { setToast } from "../../../util/toast";
import { getDefaultCurrency } from "../../store/currency/currency.action";
import InfoTooltip from "../../extra/InfoTooltip";
import {
  androidAdsContent,
  iosAdsContent,
  imaAdsTagsContent,
  videoAdRollContent,
} from "../../extra/infoContent";
import {
  formatMidrollPositions,
  getAdRollSettings,
  isSameMidrollPositions,
  parseMidrollPositions,
  validateAdTagUrl,
} from "../../../util/adRollSettings";

const AD_ROLL_SWITCHES = [
  {
    type: "isPrerollEnabled",
    label: "Enable Preroll Ads",
    description: "Play an ad before the video starts.",
    icon: "bi bi-skip-start-circle",
  },
  {
    type: "isMidrollEnabled",
    label: "Enable Midroll Ads",
    description: "Play ads at the configured midroll positions during the video.",
    icon: "bi bi-pause-circle",
  },
  {
    type: "isPostrollEnabled",
    label: "Enable Postroll Ads",
    description: "Play an ad after the video ends using the Postroll Ad Tag URL.",
    icon: "bi bi-skip-end-circle",
  },
];

/* Styled switch (correct mode check) */
const MaterialUISwitch = styled(Switch)(({ theme }) => ({
  width: 76,
  padding: 7,
  "& .MuiSwitch-switchBase": {
    margin: 1,
    padding: 0,
    top: 8,
    transform: "translateX(10px)",
    "&.Mui-checked": {
      color: "#fff",
      transform: "translateX(38px)",
      top: 8,
      "& .MuiSwitch-thumb:before": {
        backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M16.5992 5.06724C16.396 4.86409 16.1205 4.75 15.8332 4.75c-.2872 0-.5627.11409-.7659.31719L7.9166 12.2179 4.934 9.2353a1.17 1.17 0 0 0-1.7801 1.532l3.7486 3.7486c.2031.2031.4786.3172.7659.3172s.5627-.1141.7659-.3172l7.9167-7.9167c.2031-.20315.3172-.47865.3172-.76591 0-.28727-.1141-.56277-.3172-.76592Z" fill="white" stroke="white" stroke-width="0.5"/></svg>')`,
      },
      "& + .MuiSwitch-track": {
        opacity: 1,
        backgroundColor:
          theme?.palette?.mode === "dark" ? "#8796A5" : "#aab4be",
      },
    },
  },
  "& .MuiSwitch-thumb": {
    backgroundColor: theme?.palette?.mode === "dark" ? "#0FB515" : "red",
    width: 24,
    height: 24,
    "&:before": {
      content: "''",
      position: "absolute",
      inset: 0,
      backgroundRepeat: "no-repeat",
      backgroundPosition: "center",
      backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M14.1665 5.83301 5.83325 14.1663" stroke="white" stroke-width="2.5" stroke-linecap="round"/><path d="M5.83325 5.83301 14.1665 14.1663" stroke="white" stroke-width="2.5" stroke-linecap="round"/></svg>')`,
    },
  },
  "& .MuiSwitch-track": {
    border: "0.5px solid rgba(0,0,0,0.14)",
    background: "#FFEDF0",
    boxShadow: "inset 0 0 2px rgba(0,0,0,0.08)",
    opacity: 1,
    width: 60,
    height: 28,
    borderRadius: 52,
  },
}));

const MonetizationSetting = (props) => {
  const dispatch = useDispatch();
  const { settingData, adsData } = useSelector((s) => s.setting);
  const { isLoading } = useSelector((s) => s.dialogue);
  const { defaultCurrency } = useSelector((s) => s.currency);
  const hasPermission = useSelector((s) => s.admin.admin.flag);
  const isDemo = sessionStorage.getItem("demo") === "demo@admin.com";

  /* Monetize state */
  const [data, setData] = useState(null);

  const [originalData, setOriginalData] = useState({});
  const [isMonetization, setIsMonetization] = useState(false);
  const [minWatchTime, setMinWatchTime] = useState(0);
  const [minSubScriber, setMinSubScriber] = useState(0);
  const [earningPerHour, setEarningPerHour] = useState(0);
  const [adDisplayIndex, setAdDisplayIndex] = useState(1);

  /* Ads state (Android) */
  const [androidGoogleInterstitial, setAndroidGoogleInterstitial] = useState("");
  const [androidGoogleNative, setAndroidGoogleNative] = useState("");
  const [androidGoogleNativeVideo, setAndroidGoogleNativeVideo] = useState("");
  const [androidGoogleReward, setAndroidGoogleReward] = useState("");
  const [androidVideoAdUrl, setAndroidVideoAdUrl] = useState(""); // NEW (flat submit)

  /* Ads state (iOS) */
  const [iosGoogleInterstitial, setIosGoogleInterstitial] = useState("");
  const [iosGoogleNative, setIosGoogleNative] = useState("");
  const [iosGoogleNativeVideo, setIosGoogleNativeVideo] = useState("");
  const [iosGoogleReward, setIosGoogleReward] = useState("");
  const [iosVideoAdUrl, setIosVideoAdUrl] = useState(""); // NEW (flat submit)

  /* IMA Tags (comma-separated in API: imaTagsUrl) */
  const [imaTagUrl1, setImaTagUrl1] = useState("");
  const [imaTagUrl2, setImaTagUrl2] = useState("");
  const [imaTagUrl3, setImaTagUrl3] = useState("");

  const [googleAds, setGoogleAds] = useState(false);
  const [originalAdsData, setOriginalAdsData] = useState({});

  /* Video ad-roll state (stored on the setting document) */
  const [postrollAdTagUrl, setPostrollAdTagUrl] = useState("");
  const [midrollPositionsInput, setMidrollPositionsInput] = useState("");
  const [adRollSwitchPending, setAdRollSwitchPending] = useState("");

  const [error, setError] = useState({
    minWatchTime: "",
    minSubScriber: "",
    earningPerHour: "",
    adDisplayIndex: "",
    postrollAdTagUrl: "",
    midrollPositionPercent: "",
  });

  /* Bootstrap */
  useEffect(() => {
    if (isDemo) return;
    dispatch(getSettingApi());
    dispatch(getDefaultCurrency());
    dispatch(getAdsApi());
  }, [dispatch, isDemo]);

  /* Store → local (Monetize) */
  useEffect(() => {
    if (!settingData) return;
    setData(settingData);
    setIsMonetization(Boolean(settingData?.isMonetization));
    setMinWatchTime(Number(settingData?.minWatchTime || 0));
    setMinSubScriber(Number(settingData?.minSubScriber || 0));
    setEarningPerHour(Number(settingData?.earningPerHour || 0));
    setAdDisplayIndex(
      Number.isFinite(Number(settingData?.adDisplayIndex))
        ? Number(settingData?.adDisplayIndex)
        : 1
    );

    // Set original data for comparison
    setOriginalData({
      minWatchTime: Number(settingData?.minWatchTime || 0),
      minSubScriber: Number(settingData?.minSubScriber || 0),
      earningPerHour: Number(settingData?.earningPerHour || 0),
      adDisplayIndex: Number.isFinite(Number(settingData?.adDisplayIndex))
        ? Number(settingData?.adDisplayIndex)
        : 1,
    });
  }, [settingData]);

  /* Store → local (Video ad-roll). Keyed on the saved values so a toggle
     response does not wipe unsaved edits in the text inputs. */
  const adRollSaved = useMemo(() => getAdRollSettings(settingData), [settingData]);
  const savedMidrollDisplay = formatMidrollPositions(adRollSaved.midrollPositionPercent);

  useEffect(() => {
    setPostrollAdTagUrl(adRollSaved.postrollAdTagUrl);
    setError((prev) => ({ ...prev, postrollAdTagUrl: "" }));
  }, [adRollSaved.postrollAdTagUrl, adRollSaved.isPostrollEnabled]);

  useEffect(() => {
    setMidrollPositionsInput(savedMidrollDisplay);
    setError((prev) => ({ ...prev, midrollPositionPercent: "" }));
  }, [savedMidrollDisplay]);

  /* Store → local (Ads, read nested, keep flat state) */
  useEffect(() => {
    if (!adsData) return;

    setAndroidGoogleInterstitial(adsData?.android?.google?.interstitial || "");
    setAndroidGoogleNative(adsData?.android?.google?.native || "");
    setAndroidGoogleNativeVideo(adsData?.android?.google?.nativeAdVideo || "");
    setAndroidGoogleReward(adsData?.android?.google?.reward || "");
    setAndroidVideoAdUrl(adsData?.android?.google?.videoAdUrl || ""); // NEW

    setIosGoogleInterstitial(adsData?.ios?.google?.interstitial || "");
    setIosGoogleNative(adsData?.ios?.google?.native || "");
    setIosGoogleNativeVideo(adsData?.ios?.google?.nativeAdVideo || "");
    setIosGoogleReward(adsData?.ios?.google?.reward || "");
    setIosVideoAdUrl(adsData?.ios?.google?.videoAdUrl || ""); // NEW

    const rawImaTags =
      adsData?.imaTagsUrl ??
      adsData?.google?.imaTagsUrl ??
      adsData?.android?.google?.imaTagsUrl ??
      "";
    const parsedImaTags = String(rawImaTags)
      .split(",")
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    setImaTagUrl1(parsedImaTags[0] || "");
    setImaTagUrl2(parsedImaTags[1] || "");
    setImaTagUrl3(parsedImaTags[2] || "");

    setGoogleAds(Boolean(adsData?.isGoogle));

    // Set original ads data for comparison
    setOriginalAdsData({
      androidGoogleInterstitial: adsData?.android?.google?.interstitial || "",
      androidGoogleNative: adsData?.android?.google?.native || "",
      androidGoogleNativeVideo: adsData?.android?.google?.nativeAdVideo || "",
      androidGoogleReward: adsData?.android?.google?.reward || "",
      androidVideoAdUrl: adsData?.android?.google?.videoAdUrl || "",
      iosGoogleInterstitial: adsData?.ios?.google?.interstitial || "",
      iosGoogleNative: adsData?.ios?.google?.native || "",
      iosGoogleNativeVideo: adsData?.ios?.google?.nativeAdVideo || "",
      iosGoogleReward: adsData?.ios?.google?.reward || "",
      iosVideoAdUrl: adsData?.ios?.google?.videoAdUrl || "",
      imaTagsUrl: String(rawImaTags || ""),
    });
  }, [adsData]);

  /* Helpers */
  const guardPermission = async () => {
    if (!hasPermission) {
      await permissionError();
      return false;
    }
    return true;
  };

  const validateMonetizeForm = () => {
    const errs = {};
    if (minWatchTime === "" || minWatchTime === null) {
      errs.minWatchTime = "Min Watch Time is required !";
    } else if (!Number.isFinite(Number(minWatchTime)) || Number(minWatchTime) <= 0) {
      errs.minWatchTime = "Min Watch Time must be > 0.";
    }

    if (minSubScriber === "" || minSubScriber === null) {
      errs.minSubScriber = "Min Subscriber is required !";
    } else if (!Number.isFinite(Number(minSubScriber)) || Number(minSubScriber) < 0) {
      errs.minSubScriber = "Min Subscriber must be ≥ 0.";
    }

    if (earningPerHour === "" || earningPerHour === null) {
      errs.earningPerHour = "Earning per hour is required !";
    } else if (!Number.isFinite(Number(earningPerHour)) || Number(earningPerHour) < 0) {
      errs.earningPerHour = "Earning per hour must be ≥ 0.";
    }

    if (adDisplayIndex === "" || adDisplayIndex === null) {
      errs.adDisplayIndex = "Ad Display Frequency is required !";
    } else if (
      !Number.isFinite(Number(adDisplayIndex)) ||
      Number(adDisplayIndex) <= 0
    ) {
      errs.adDisplayIndex = "Ad Display Frequency must be > 0.";
    }

    setError((prev) => ({ ...prev, ...errs }));
    return Object.keys(errs).length === 0;
  };

  /* Returns the normalized ad-roll values, or null when invalid. */
  const validateAdRollForm = () => {
    const nextPostrollUrl = String(postrollAdTagUrl || "").trim();
    const urlError =
      nextPostrollUrl !== adRollSaved.postrollAdTagUrl
        ? validateAdTagUrl(nextPostrollUrl)
        : "";
    const midroll = parseMidrollPositions(midrollPositionsInput);

    setError((prev) => ({
      ...prev,
      postrollAdTagUrl: urlError,
      midrollPositionPercent: midroll.error || "",
    }));

    if (urlError || midroll.error) return null;
    return {
      postrollAdTagUrl: nextPostrollUrl,
      midrollPositionPercent: midroll.value,
    };
  };

  /* Handlers */
  const handleSubmit = async () => {
    if (!(await guardPermission())) return;
    const isMonetizeValid = validateMonetizeForm();
    const adRoll = validateAdRollForm();
    if (!isMonetizeValid || !adRoll) return;

    // Only send changed fields
    let payload = {};

    if (Number(minWatchTime) !== originalData.minWatchTime) {
      payload.minWatchTime = Number(minWatchTime);
    }
    if (Number(minSubScriber) !== originalData.minSubScriber) {
      payload.minSubScriber = Number(minSubScriber);
    }
    if (Math.max(1, Number(adDisplayIndex) || 1) !== originalData.adDisplayIndex) {
      payload.adDisplayIndex = Math.max(1, Number(adDisplayIndex) || 1);
    }
    if (Number(earningPerHour) !== originalData.earningPerHour) {
      payload.earningPerHour = Number(earningPerHour);
    }
    if (adRoll.postrollAdTagUrl !== adRollSaved.postrollAdTagUrl) {
      payload.postrollAdTagUrl = adRoll.postrollAdTagUrl;
    }
    if (
      !isSameMidrollPositions(
        adRoll.midrollPositionPercent,
        adRollSaved.midrollPositionPercent
      )
    ) {
      payload.midrollPositionPercent = adRoll.midrollPositionPercent;
    }

    // If no changes, don't call API
    if (Object.keys(payload).length === 0) {
      return "noChanges";
    }

    props.editSetting(data?._id, payload);
    return "updated";
  };

  // ⬇️ Only send changed fields
  const handleSubmitAds = async () => {
    if (!(await guardPermission())) return;

    let adsDataApi = {};

    if (androidGoogleInterstitial !== originalAdsData.androidGoogleInterstitial) {
      adsDataApi.androidGoogleInterstitial = androidGoogleInterstitial;
    }
    if (androidGoogleNative !== originalAdsData.androidGoogleNative) {
      adsDataApi.androidGoogleNative = androidGoogleNative;
    }
    if (androidGoogleNativeVideo !== originalAdsData.androidGoogleNativeVideo) {
      adsDataApi.androidNativeAdVideo = androidGoogleNativeVideo;
    }
    if (iosGoogleNativeVideo !== originalAdsData.iosGoogleNativeVideo) {
      adsDataApi.iosNativeAdVideo = iosGoogleNativeVideo;
    }
    if (iosGoogleNative !== originalAdsData.iosGoogleNative) {
      adsDataApi.iosGoogleNative = iosGoogleNative;
    }
    if (iosGoogleInterstitial !== originalAdsData.iosGoogleInterstitial) {
      adsDataApi.iosGoogleInterstitial = iosGoogleInterstitial;
    }
    if (androidGoogleReward !== originalAdsData.androidGoogleReward) {
      adsDataApi.androidGoogleReward = androidGoogleReward;
    }
    if (iosGoogleReward !== originalAdsData.iosGoogleReward) {
      adsDataApi.iosGoogleReward = iosGoogleReward;
    }
    if (androidVideoAdUrl !== originalAdsData.androidVideoAdUrl) {
      adsDataApi.androidGoogleVideoAdUrl = androidVideoAdUrl;
    }
    if (iosVideoAdUrl !== originalAdsData.iosVideoAdUrl) {
      adsDataApi.iosGoogleVideoAdUrl = iosVideoAdUrl;
    }

    const nextImaTagsUrl = `${String(imaTagUrl1 || "").trim()},${String(
      imaTagUrl2 || ""
    ).trim()},${String(imaTagUrl3 || "").trim()}`;
    if (nextImaTagsUrl !== String(originalAdsData.imaTagsUrl || "")) {
      adsDataApi.imaTagsUrl = nextImaTagsUrl;
    }

    // If no changes, don't call API
    if (Object.keys(adsDataApi).length === 0) {
      return "noChanges";
    }

    dispatch(adsApiData(adsDataApi, adsData?._id));
    return "updated";
  };

  useEffect(() => {
    if (props.submitTrigger > 0) {
      const onSubmit = async () => {
        const monetizeResult = await handleSubmit();
        const adsResult = await handleSubmitAds();
        if (monetizeResult === "noChanges" && adsResult === "noChanges") {
          setToast("info", "No changes made");
        }
      };
      onSubmit();
    }
  }, [props.submitTrigger]);

  const handleChangeSwitch = async (method) => {
    if (!(await guardPermission())) return;
    try {
      if (method === "monetization") {
        const next = !isMonetization;
        setIsMonetization(next);
        await props.switchApi(data?._id, method, next); // send NEW value
      }
    } catch (e) {
      console.error("Error updating switch:", e);
    }
  };

  /* The switch reflects the store value, which only changes when the API succeeds. */
  const handleAdRollSwitch = async (type) => {
    if (adRollSwitchPending) return;
    if (!(await guardPermission())) return;
    if (!data?._id) return;
    setAdRollSwitchPending(type);
    try {
      await props.switchApi(data._id, type, adRollSaved[type]);
    } finally {
      setAdRollSwitchPending("");
    }
  };

  const handleChangeAds = async () => {
    if (!(await guardPermission())) return;
    const previous = googleAds;
    const next = !previous;
    setGoogleAds(next);
    const updated = await dispatch(isAdsChange(next, adsData?._id)); // pass desired next state
    if (!updated) setGoogleAds(previous);
  };

  const currencySymbol = useMemo(
    () => (defaultCurrency?.symbol ? defaultCurrency.symbol : ""),
    [defaultCurrency?.symbol]
  );

  return (
    <div className="payment-setting p-0 mainSetting">
      {/* ===== Monetize Setting ===== */}
      <div className="settingBox row g-3">
        <div className="col-12 col-md-6">
          <div className="settingBoxOuter">
            <div className="settingBoxHeader d-flex justify-content-between align-items-center px-2">
              <h4 className="settingboxheader">Monetization</h4>
            </div>

            <div className="setting-box-body">
              <div className="row">
                <div className="col-12 d-flex justify-content-between align-items-center ">
                  <p className="m-0 fw-medium">
                    <span>Monetization Switch (enable/disable monetization in app)</span>
                  </p>
                  {isLoading ? (
                    <Skeleton variant="rectangular" width="60px" height={28} style={{ borderRadius: '52px' }} />
                  ) : (
                    <FormControlLabel
                      control={
                        <MaterialUISwitch
                          sx={{ m: 1 }}
                          checked={Boolean(isMonetization)}
                          onChange={() => handleChangeSwitch("monetization")}
                        />
                      }
                    />
                  )}
                </div>

                <div className="col-12 withdrawal-input border-setting">
                  <Input
                    label={"Min Watch Time of hours of Viewership in channel Required for Monetization"}
                    name={"minWatchTime"}
                    type={"number"}
                    loading={isLoading}
                    value={minWatchTime}
                    errorMessage={error.minWatchTime}
                    placeholder={isDemo ? "1500" : "Enter Detail..."}
                    onChange={(e) => {
                      const value = e.target.value;
                      const numericValue = Number(value);

                      setMinWatchTime(value);

                      setError((prev) => {
                        let message = "";
                        if (value === "") {
                          message = "Min Watch Time is required !";
                        } else if (
                          !Number.isFinite(numericValue) ||
                          numericValue <= 0
                        ) {
                          message = "Min Watch Time must be > 0.";
                        }
                        return { ...prev, minWatchTime: message };
                      });
                    }}
                  />
                </div>

                <div className="col-12 withdrawal-input border-setting">
                  <Input
                    label={"Minimum Subscriber Count for Monetization Eligibility"}
                    name={"minSubScriber"}
                    type={"number"}
                    loading={isLoading}
                    value={minSubScriber}
                    errorMessage={error.minSubScriber}
                    placeholder={isDemo ? "1000" : "Enter Detail..."}
                    onChange={(e) => {
                      const value = e.target.value;
                      const numericValue = Number(value);

                      setMinSubScriber(value);

                      setError((prev) => {
                        let message = "";
                        if (value === "") {
                          message = "Min Subscriber is required !";
                        } else if (
                          !Number.isFinite(numericValue) ||
                          numericValue < 0
                        ) {
                          message = "Min Subscriber must be ≥ 0.";
                        }
                        return { ...prev, minSubScriber: message };
                      });
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Earnings */}
        <div className="col-12 col-md-6">
          <div className="settingBoxOuter">
            <div className="settingBoxHeader d-flex justify-content-between align-items-center px-2">
              <h4 className="settingboxheader">Set Earnings Rate Per Hour Of Viewership For Creator</h4>
            </div>

            <div className="setting-box-body">
              <div className="row withdrawal-input">
                <div className="row">
                  <div className="col-5">
                    <Input label={"Hour"} name={"Hour"} type={"number"} loading={isLoading} value={1} disabled />
                  </div>
                  <div className="col-1" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <p className="mb-0 mt-4" style={{ fontSize: 22 }}>=</p>
                  </div>
                  <div className="col-6">
                    <Input
                      label={`Earning (${currencySymbol})`}
                      name={"earningPerHour"}
                      type={"number"}
                      loading={isLoading}
                      value={earningPerHour}
                      errorMessage={error.earningPerHour}
                      placeholder={isDemo ? "3.25" : "Enter Detail..."}
                      onChange={(e) => {
                        const value = e.target.value;
                        const numericValue = Number(value);

                        setEarningPerHour(value);

                        setError((prev) => {
                          let message = "";
                          if (value === "") {
                            message = "Earning per hour is required !";
                          } else if (
                            !Number.isFinite(numericValue) ||
                            numericValue < 0
                          ) {
                            message = "Earning per hour must be ≥ 0.";
                          }
                          return { ...prev, earningPerHour: message };
                        });
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Ad frequency */}
        <div className="col-12">
          <div className="settingBoxOuter">
            <div className="settingBoxHeader d-flex justify-content-between align-items-center px-2">
              <h4 className="settingboxheader">Show Ads After Every {Math.max(1, Number(adDisplayIndex) || 1)} Video Views</h4>
            </div>

            <div className="setting-box-body">
              <div className="row withdrawal-input">
                <div className="row">
                  <div className="col-12">
                    <Input
                      label={"Ad Display Frequency (Number of Videos)"}
                      name={"adDisplayIndex"}
                      type={"number"}
                      loading={isLoading}
                      value={adDisplayIndex}
                      errorMessage={error.adDisplayIndex}
                      placeholder={isDemo ? "2" : "Enter Detail..."}
                      onChange={(e) => {
                        const value = e.target.value;
                        const numericValue = Number(value);

                        setAdDisplayIndex(value);

                        setError((prev) => {
                          let message = "";
                          if (value === "") {
                            message = "Ad Display Frequency is required !";
                          } else if (
                            !Number.isFinite(numericValue) ||
                            numericValue <= 0
                          ) {
                            message = "Ad Display Frequency must be > 0.";
                          }
                          return { ...prev, adDisplayIndex: message };
                        });
                      }}
                    />
                  </div>
                  <div className="col-1" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ===== Ads Setting ===== */}
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3 mt-4 px-1">
        <h4 className="settingboxheader mb-0">Ads Setting</h4>
      </div>

      <div className="settingBox row g-3">
        <div className="col-12 ">
          <div className="settingBoxOuter">
            <div className="settingBoxHeader d-flex justify-content-between align-items-center px-2">
              <h4 className="settingboxheader">Google Ad</h4>
            </div>

            <div className="setting-box-body">
              <div className="row">
                <div className="col-12 d-flex justify-content-between align-items-center ">
                  <p className="fw-medium m-0">
                    <span>Google Ad (enable/disable google ads in app)</span>
                  </p>
                  {isLoading ? (
                    <Skeleton variant="rectangular" width="60px" height={28} style={{ borderRadius: '52px' }} />
                  ) : (
                    <FormControlLabel
                      control={
                        <MaterialUISwitch
                          sx={{ m: 1 }}
                          checked={Boolean(googleAds)}
                          onChange={handleChangeAds}
                        />
                      }
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* IMA Ads Tags */}
        <div className="col-12">
          <div className="settingBoxOuter ima-tags-box">
            <div className="settingBoxHeader d-flex justify-content-between align-items-center px-2">
              <h4 className="settingboxheader">IMA Ads Tags</h4>
              <InfoTooltip title="IMA Ads Tags (Web)" content={imaAdsTagsContent} />
            </div>

            <div className="setting-box-body">
              <div className="col-12 withdrawal-input border-setting pt-2 mt-0">
                <Input
                  labelShow={false}
                  label={"IMA Tag URL 1"}
                  name={"imaTagUrl1"}
                  type={"text"}
                  loading={isLoading}
                  value={imaTagUrl1}
                  fieldClass={"ima-tag-input"}
                  placeholder={
                    isDemo
                      ? "https://pubads.g.doubleclick.net/gampad/ads?iu=/21775744923/external/single_preroll_skippable&sz=640x480..."
                      : "Enter IMA Tag URL 1..."
                  }
                  onChange={(e) => setImaTagUrl1(e.target.value)}
                />
              </div>
              <div className="col-12 withdrawal-input border-setting pt-2 mt-2">
                <Input
                  labelShow={false}
                  label={"IMA Tag URL 2"}
                  name={"imaTagUrl2"}
                  type={"text"}
                  loading={isLoading}
                  value={imaTagUrl2}
                  fieldClass={"ima-tag-input"}
                  placeholder={
                    isDemo
                      ? "https://pubads.g.doubleclick.net/gampad/ads?iu=/21775744923/external/single_ad_samples&sz=640x480..."
                      : "Enter IMA Tag URL 2..."
                  }
                  onChange={(e) => setImaTagUrl2(e.target.value)}
                />
              </div>
              <div className="col-12 withdrawal-input border-setting pt-2 mt-2">
                <Input
                  labelShow={false}
                  label={"IMA Tag URL 3"}
                  name={"imaTagUrl3"}
                  type={"text"}
                  loading={isLoading}
                  value={imaTagUrl3}
                  fieldClass={"ima-tag-input"}
                  placeholder={
                    isDemo
                      ? "https://pubads.g.doubleclick.net/gampad/ads?sz=640x480&iu=/124319096/external/ad_rule_samples&ciu_szs=300x250..."
                      : "Enter IMA Tag URL 3..."
                  }
                  onChange={(e) => setImaTagUrl3(e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Video Ad-Roll Settings */}
        <div className="col-12">
          <div className="settingBoxOuter">
            <div className="settingBoxHeader d-flex justify-content-between align-items-center px-2">
              <h4 className="settingboxheader">Video Ad-Roll Settings</h4>
              <InfoTooltip title="Video Ad-Roll Settings" content={videoAdRollContent} />
            </div>

            <div className="setting-box-body ad-roll-body">
              <p className="ad-roll-section-label">Ad Breaks</p>
              <div className="payment-toggle-stack ad-roll-toggle-stack">
                {AD_ROLL_SWITCHES.map(({ type, label, description, icon }) => {
                  const isOn = Boolean(adRollSaved[type]);
                  return (
                    <div key={type} className="payment-toggle-row ad-roll-toggle-row">
                      <div className="ad-roll-toggle-meta">
                        <span
                          className={`ad-roll-toggle-icon${isOn ? " is-on" : ""}`}
                          aria-hidden
                        >
                          <i className={icon} />
                        </span>
                        <div className="payment-toggle-label">
                          <span className="ad-roll-toggle-title">{label}</span>
                          <span className="payment-toggle-hint ad-roll-toggle-hint">
                            {description}
                          </span>
                        </div>
                      </div>
                      {isLoading ? (
                        <Skeleton variant="rectangular" width="60px" height={28} style={{ borderRadius: '52px' }} />
                      ) : (
                        <FormControlLabel
                          className="ad-roll-switch"
                          control={
                            <MaterialUISwitch
                              checked={isOn}
                              disabled={Boolean(adRollSwitchPending)}
                              inputProps={{ "aria-label": label }}
                              onChange={() => handleAdRollSwitch(type)}
                            />
                          }
                        />
                      )}
                    </div>
                  );
                })}
              </div>

              <p className="ad-roll-section-label">Ad Configuration</p>
              <div className="row g-3 ad-roll-fields">
                <div
                  className={`col-12 col-lg-6 ad-roll-field${
                    adRollSaved.isPostrollEnabled ? "" : " is-disabled"
                  }`}
                >
                  <Input
                    label={"Postroll Ad Tag URL"}
                    name={"postrollAdTagUrl"}
                    type={"text"}
                    loading={isLoading}
                    value={postrollAdTagUrl}
                    disabled={!adRollSaved.isPostrollEnabled}
                    errorMessage={error.postrollAdTagUrl}
                    placeholder={"https://pubads.g.doubleclick.net/gampad/ads?..."}
                    onChange={(e) => {
                      const value = e.target.value;
                      setPostrollAdTagUrl(value);
                      setError((prev) => ({
                        ...prev,
                        postrollAdTagUrl: validateAdTagUrl(value),
                      }));
                    }}
                  />
                  {!isLoading && (
                    <p className="ad-roll-hint">
                      <i
                        className={`bi ${
                          adRollSaved.isPostrollEnabled ? "bi-info-circle" : "bi-lock"
                        }`}
                        aria-hidden
                      />
                      <span>
                        {adRollSaved.isPostrollEnabled
                          ? "IMA ad tag URL used for postroll ad playback after the video ends. Leave empty to clear it."
                          : "Enable Postroll Ads to edit this URL. The saved URL is kept while postroll is off."}
                      </span>
                    </p>
                  )}
                </div>

                <div className="col-12 col-lg-6 ad-roll-field">
                  <Input
                    label={"Midroll Positions (%)"}
                    name={"midrollPositionPercent"}
                    type={"text"}
                    loading={isLoading}
                    value={midrollPositionsInput}
                    errorMessage={error.midrollPositionPercent}
                    placeholder={"e.g. 25%, 50%, 75%"}
                    onChange={(e) => {
                      const value = e.target.value;
                      setMidrollPositionsInput(value);
                      setError((prev) => ({
                        ...prev,
                        midrollPositionPercent: parseMidrollPositions(value).error || "",
                      }));
                    }}
                  />
                  {!isLoading && (
                    <p className="ad-roll-hint">
                      <i className="bi bi-info-circle" aria-hidden />
                      <span>
                        Add multiple midroll cue points for a video as comma-separated
                        percentages (0–100) of its duration, e.g. 25, 50, 75. Duplicates
                        are removed and values are sorted on save. Leave empty for none.
                      </span>
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Android / iOS fields */}
        <div className="col-12 col-md-6">
          <div className="settingBoxOuter">
            <div className="settingBoxHeader d-flex justify-content-between align-items-center px-2">
              <h4 className="settingboxheader">Android</h4>
              <InfoTooltip title="Android Ads Setting" content={androidAdsContent} />
            </div>

            <div className="setting-box-body">
              <div className="col-12 withdrawal-input border-setting">
                <Input
                  label={"Android Google Interstitial"}
                  name={"androidGoogleInterstitial"}
                  type={"text"}
                  loading={isLoading}
                  value={androidGoogleInterstitial}
                  placeholder={isDemo ? "ca-app-pub-3940256099942544/1033173712" : "Enter Detail..."}
                  onChange={(e) => setAndroidGoogleInterstitial(e.target.value)}
                />
              </div>

              <div className="col-12 withdrawal-input border-setting">
                <Input
                  label={"Android Google Native Image Ad"}
                  name={"androidGoogleNative"}
                  type={"text"}
                  loading={isLoading}
                  value={androidGoogleNative}
                  placeholder={isDemo ? "ca-app-pub-3940256099942544/2247696110" : "Enter Detail..."}
                  onChange={(e) => setAndroidGoogleNative(e.target.value)}
                />
              </div>

              <div className="col-12 withdrawal-input border-setting">
                <Input
                  label={"Android Google Native Video Ad"}
                  name={"androidGoogleNativeVideo"}
                  type={"text"}
                  loading={isLoading}
                  value={androidGoogleNativeVideo}
                  placeholder={isDemo ? "ca-app-pub-3940256099942544/2247696110" : "Enter Detail..."}
                  onChange={(e) => setAndroidGoogleNativeVideo(e.target.value)}
                />
              </div>

              <div className="col-12 withdrawal-input border-setting">
                <Input
                  label={"Android Google Reward"}
                  name={"androidGoogleReward"}
                  type={"text"}
                  loading={isLoading}
                  value={androidGoogleReward}
                  placeholder={isDemo ? "ca-app-pub-3940256099942544/5224354917" : "Enter Detail..."}
                  onChange={(e) => setAndroidGoogleReward(e.target.value)}
                />
              </div>

              <div className="col-12 withdrawal-input border-setting">
                <Input
                  label={"Android Interactive Video Ad URL"}
                  name={"androidVideoAdUrl"}
                  type={"text"}
                  loading={isLoading}
                  value={androidVideoAdUrl}
                  placeholder={isDemo ? "android_video_ad_url_id" : "Enter Detail..."}
                  onChange={(e) => setAndroidVideoAdUrl(e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>

        {/* iOS */}
        <div className="col-12 col-md-6">
          <div className="settingBoxOuter">
            <div className="settingBoxHeader d-flex justify-content-between align-items-center px-2">
              <h4 className="settingboxheader">iOS</h4>
              <InfoTooltip title="iOS Ads Setting" content={iosAdsContent} />
            </div>

            <div className="setting-box-body">
              <div className="col-12 withdrawal-input border-setting">
                <Input
                  label={"iOS Google Interstitial"}
                  name={"iosGoogleInterstitial"}
                  type={"text"}
                  loading={isLoading}
                  value={iosGoogleInterstitial}
                  placeholder={isDemo ? "ca-app-pub-3940256099942544/4411468910" : "Enter Detail..."}
                  onChange={(e) => setIosGoogleInterstitial(e.target.value)}
                />
              </div>

              <div className="col-12 withdrawal-input border-setting">
                <Input
                  label={"iOS Google Native Image Ad"}
                  name={"iosGoogleNative"}
                  type={"text"}
                  loading={isLoading}
                  value={iosGoogleNative}
                  placeholder={isDemo ? "ca-app-pub-3940256099942544/3986624511" : "Enter Detail..."}
                  onChange={(e) => setIosGoogleNative(e.target.value)}
                />
              </div>

              <div className="col-12 withdrawal-input border-setting">
                <Input
                  label={"iOS Google Native Video Ad"}
                  name={"iosGoogleNativeVideo"}
                  type={"text"}
                  loading={isLoading}
                  value={iosGoogleNativeVideo}
                  placeholder={isDemo ? "ca-app-pub-3940256099942544/3986624511" : "Enter Detail..."}
                  onChange={(e) => setIosGoogleNativeVideo(e.target.value)}
                />
              </div>

              <div className="col-12 withdrawal-input border-setting">
                <Input
                  label={"iOS Google Reward"}
                  name={"iosGoogleReward"}
                  type={"text"}
                  loading={isLoading}
                  value={iosGoogleReward}
                  placeholder={isDemo ? "ca-app-pub-3940256099942544/1712485313" : "Enter Detail..."}
                  onChange={(e) => setIosGoogleReward(e.target.value)}
                />
              </div>

              <div className="col-12 withdrawal-input border-setting">
                <Input
                  label={"iOS Interactive Video Ad URL"}
                  name={"iosVideoAdUrl"}
                  type={"text"}
                  loading={isLoading}
                  value={iosVideoAdUrl}
                  placeholder={isDemo ? "ios_video_ad_url_id" : "Enter Detail..."}
                  onChange={(e) => setIosVideoAdUrl(e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default connect(null, {
  getSettingApi,
  editSetting,
  switchApi,
  getAdsApi,
  getWithdrawalApi,
})(MonetizationSetting);
